import { useState, useEffect, forwardRef, useImperativeHandle, ForwardRefRenderFunction } from 'react';
import { colors } from '../../../styles/colors';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PieChart } from '@mui/x-charts/PieChart';
import { getWeeklyTransactionStatusSummary } from '../../../services/transactionService';
import dayjs from 'dayjs';

// For TypeScript support
export type WeeklyTransactionsStatusCardRef = {
  fetchData: () => Promise<void>;
};

const WeeklyTransactionsStatusCardComponent: ForwardRefRenderFunction<WeeklyTransactionsStatusCardRef> = (_, ref) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ successCount: 0, failureCount: 0 });
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    fetchData
  }));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const summary = await getWeeklyTransactionStatusSummary();
      setData(summary);
    } catch (error) {
      console.error('Failed to fetch weekly transaction status summary:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading...</Typography>
        </CardContent>
      </Card>
    );
  }

  const pieData = [
    { id: 0, value: data.successCount, label: 'Success', color: colors.success },
    { id: 1, value: data.failureCount, label: 'Failed', color: colors.failed }
  ];

  const totalCount = data.successCount + data.failureCount;
  const successRate = totalCount > 0 
    ? Math.round((data.successCount / totalCount) * 100) 
    : 0;

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Weekly Transactions Status
        </Typography>
        
        <Box sx={{ height: 150, display: 'flex', justifyContent: 'center', position: 'relative' }}>
          <PieChart
            series={[
              {
                data: pieData,
                highlightScope: { faded: 'global', highlighted: 'item' },
                faded: { additionalRadius: -30 },
              },
            ]}
            height={150}
            width={260}
          />
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="h4" color="primary">
            {successRate}%
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Success Rate
          </Typography>
       
          <Typography variant="body2" color="text.secondary">
            Success: {data.successCount} | Failed: {data.failureCount}
          </Typography>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => {
              const today = dayjs();
              const sevenDaysAgo = dayjs().subtract(7, 'day');
              const dateFilter = `dateReceivedByNASHStart=${sevenDaysAgo.format('YYYY-MM-DD')}&dateReceivedByNASHEnd=${today.format('YYYY-MM-DD')}`;
              navigate(`/transactions?${dateFilter}`)
            }}
          >
            View Last 7 Days
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const WeeklyTransactionsStatusCard = forwardRef(WeeklyTransactionsStatusCardComponent);
export default WeeklyTransactionsStatusCard;
